import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, PlayCircle, Newspaper, Headphones, Clock, ArrowRight } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Kicker, WfButton, Tag } from "@/components/site/primitives";
import {
  MEDIA_HERO,
  MEDIA_SECTIONS,
  IN_THE_NEWS_INTRO,
  NEWS_ARCHIVE,
  NEWS_FEATURED,
  NEWS_BY_YEAR,
  ANNOUNCEMENTS_INTRO,
  ANNOUNCEMENTS,
  BLOG_INTRO,
  BLOG_PLACEHOLDERS,
  MEDIA_CONTACT,
} from "@/content/media";
import { scrollToId } from "@/lib/scrollTo";

const TYPE_ICON = { pdf: FileText, video: PlayCircle, article: Newspaper, audio: Headphones };
const TYPE_LABEL = { pdf: "PDF", video: "Video", article: "Article", audio: "Audio" };

/* ---------------- Section jump nav ---------------- */
const SectionNav = () => (
  <nav aria-label="Media sections" className="border-b border-[var(--wf-border)] bg-[var(--wf-paper)]">
    <div className="wf-container">
      <ul className="flex gap-2 overflow-x-auto py-3">
        {MEDIA_SECTIONS.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              onClick={(e) => { e.preventDefault(); scrollToId(s.id); }}
              data-testid={`media-jump-${s.id}`}
              className="inline-flex min-h-11 items-center whitespace-nowrap rounded-full border border-[var(--wf-border)] bg-[var(--wf-cream)] px-5 text-sm font-semibold text-[var(--wf-ink)] transition-colors duration-150 hover:border-[var(--wf-maroon)] hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </nav>
);

/* ---------------- News item ---------------- */
const NewsItem = ({ item, testid }) => {
  const Icon = TYPE_ICON[item.type] || Newspaper;
  return (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="group flex h-full items-start gap-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5 shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-200 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
    >
      <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]" aria-hidden="true">
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--wf-green-text)]">
          {item.publication}
          <span aria-hidden="true" className="text-[var(--wf-border)]">&middot;</span>
          <span className="font-medium normal-case tracking-normal text-muted-foreground">{item.date}</span>
        </span>
        <span className="mt-1.5 flex items-start gap-1.5 font-serif text-[0.98rem] font-semibold leading-6 text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">
          {item.title}
          <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-[var(--wf-maroon)]" aria-hidden="true" />
        </span>
        <span className="sr-only"> ({TYPE_LABEL[item.type] || "Link"}, opens in a new tab)</span>
      </span>
    </a>
  );
};

/* ---------------- In the news ---------------- */
const InTheNewsSection = () => {
  const [showAll, setShowAll] = useState(false);

  return (
    <section id="in-the-news" aria-labelledby="media-news-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
      <div className="wf-container">
        <Reveal className="max-w-3xl">
          <Kicker>{IN_THE_NEWS_INTRO.eyebrow}</Kicker>
          <h2 id="media-news-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
            {IN_THE_NEWS_INTRO.title}
          </h2>
          <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">{IN_THE_NEWS_INTRO.body}</p>
        </Reveal>

        {!showAll ? (
          <ul className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="media-news-featured">
            {NEWS_FEATURED.map((item, i) => (
              <li key={item.href}>
                <NewsItem item={item} testid={`media-news-item-${i}`} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-10 space-y-12" data-testid="media-news-archive">
            {NEWS_BY_YEAR.map((bucket) => (
              <section key={bucket.year} aria-labelledby={`news-year-${bucket.year}`}>
                <div className="flex items-center gap-4">
                  <h3 id={`news-year-${bucket.year}`} className="font-serif text-2xl font-semibold text-[var(--wf-maroon)]">{bucket.year}</h3>
                  <span aria-hidden="true" className="h-px flex-1 bg-[var(--wf-border)]" />
                  <span className="text-xs font-medium text-muted-foreground">{bucket.items.length} {bucket.items.length === 1 ? "story" : "stories"}</span>
                </div>
                <ul className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {bucket.items.map((item, i) => (
                    <li key={item.href}>
                      <NewsItem item={item} testid={`media-news-${bucket.year}-${i}`} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <div className="mt-10 text-center">
          <button
            type="button"
            onClick={() => {
              setShowAll((v) => !v);
              if (showAll) scrollToId("in-the-news");
            }}
            data-testid="media-news-toggle"
            aria-expanded={showAll}
            className="inline-flex min-h-11 items-center gap-2 rounded-sm border border-[var(--wf-maroon)] px-6 py-3 text-sm font-semibold text-[var(--wf-maroon)] transition-colors duration-200 hover:bg-[var(--wf-maroon)] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
          >
            {showAll ? "Show highlights only" : `Show all coverage (${NEWS_ARCHIVE.length})`}
          </button>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Announcements ---------------- */
const AnnouncementsSection = () => (
  <section id="announcements" aria-labelledby="media-announcements-heading" className="wf-section scroll-mt-20 bg-white">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <Kicker>{ANNOUNCEMENTS_INTRO.eyebrow}</Kicker>
        <h2 id="media-announcements-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          {ANNOUNCEMENTS_INTRO.title}
        </h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">{ANNOUNCEMENTS_INTRO.body}</p>
      </Reveal>

      <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="media-announcements-grid">
        {ANNOUNCEMENTS.map((a, i) => (
          <Reveal key={a.id} delay={0.07 * i} as="li" className="h-full">
            <a
              href={a.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`media-announcement-${a.id}`}
              className="group flex h-full flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-[var(--wf-cream)]">
                <img src={a.image} alt={a.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[600ms] ease-out motion-safe:group-hover:scale-105" />
                <span className="absolute left-4 top-4"><Tag>{a.category}</Tag></span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <p className="text-xs font-medium text-muted-foreground">{a.date}</p>
                <h3 className="mt-2 font-serif text-xl font-semibold leading-snug text-[var(--wf-ink)] group-hover:text-[var(--wf-maroon)]">{a.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-6 text-[var(--wf-soil)]">{a.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--wf-maroon)]">
                  Read more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  <span className="sr-only"> about {a.title} (opens in a new tab)</span>
                </span>
              </div>
            </a>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------------- Blog (placeholders) ---------------- */
const BlogSection = () => (
  <section id="blog" aria-labelledby="media-blog-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <Kicker>{BLOG_INTRO.eyebrow}</Kicker>
        <h2 id="media-blog-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
          {BLOG_INTRO.title}
        </h2>
        <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">{BLOG_INTRO.body}</p>
      </Reveal>

      <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="media-blog-grid">
        {BLOG_PLACEHOLDERS.map((b, i) => (
          <Reveal key={b.id} delay={0.07 * i} as="li" className="h-full">
            <div
              aria-disabled="true"
              data-testid={`media-blog-${b.id}`}
              className="flex h-full flex-col overflow-hidden rounded-sm border border-dashed border-[var(--wf-border)] bg-[var(--wf-paper)]/70"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-[var(--wf-cream)]">
                <img src={b.image} alt={b.alt} loading="lazy" className="h-full w-full object-cover opacity-80 saturate-[0.85]" />
                <span className="absolute left-4 top-4"><Tag>{b.topic}</Tag></span>
                <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-[rgba(255,253,248,0.92)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--wf-soil)] backdrop-blur-sm">
                  <Clock className="h-3 w-3" aria-hidden="true" /> Coming soon
                </span>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-serif text-xl font-semibold leading-snug text-[var(--wf-ink)]">{b.title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">Our first blog posts are being written. Check back soon.</p>
              </div>
            </div>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

/* ---------------- Media contact ---------------- */
const ContactSection = () => (
  <section aria-labelledby="media-contact-heading" className="bg-white py-16 sm:py-20 lg:py-24">
    <div className="wf-container">
      <div className="relative overflow-hidden rounded-sm bg-[var(--wf-maroon)] px-6 py-14 text-center sm:px-10 sm:py-16">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[var(--wf-clay)]/30 blur-2xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-[var(--wf-wheat)]/20 blur-2xl" />
        <div className="relative mx-auto max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]">Press</span>
          <h2 id="media-contact-heading" className="mt-4 font-serif text-3xl font-semibold leading-[1.1] text-white sm:text-4xl">{MEDIA_CONTACT.title}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-[rgba(255,253,248,0.85)]">{MEDIA_CONTACT.body}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <WfButton as={Link} to={MEDIA_CONTACT.cta.href} variant="light" withArrow className="px-7" data-testid="media-contact-button">{MEDIA_CONTACT.cta.label}</WfButton>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Page ---------------- */
export default function MediaPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...MEDIA_HERO} testid="media-hero" />
        <SectionNav />
        <InTheNewsSection />
        <AnnouncementsSection />
        <BlogSection />
        <ContactSection />
      </main>
      <SiteFooter />
    </>
  );
}
